/**
 * Map a dataset-record status to a `Badge` variant + human-readable label.
 *
 * One label per DB status — `golab_dataset_records.status` has 8 enum
 * values per `feature-spec.md` §3.2 and each gets its own UI string so
 * the badge faithfully tells you what state the record is in (which pass
 * is queued, whether the first pass is done and awaiting a second
 * annotator, whether the agreement check is mid-flight, etc).
 *
 * Variants pull from the shadcn `badgeVariants` palette in
 * `components/ui/badge.tsx`. Statuses that share a conceptual category
 * (the two `assigned_*` claims, the two `annotated_*` "in-flight" states,
 * the two gold-pool endings) reuse the same colour so the strip reads as
 * grouped without losing per-status precision in the label.
 */
import type { GoLabDatasetRecordStatus } from '../types';

export type StatusBadgeVariant =
  | 'default'
  | 'secondary'
  | 'destructive'
  | 'success'
  | 'attention';

export interface StatusBadgeDescriptor {
  variant: StatusBadgeVariant;
  label: string;
}

export const statusBadge = (
  status: GoLabDatasetRecordStatus,
): StatusBadgeDescriptor => {
  switch (status) {
    case 'pending':
      return { variant: 'secondary', label: 'Pending' };
    case 'assigned_first':
      return { variant: 'default', label: 'Assigned (1st pass)' };
    case 'assigned_second':
      return { variant: 'default', label: 'Assigned (2nd pass)' };
    case 'annotated_first':
      return { variant: 'attention', label: 'Awaiting 2nd pass' };
    case 'annotated_both':
      return { variant: 'attention', label: 'Awaiting agreement check' };
    case 'agreed':
      return { variant: 'success', label: 'Agreed' };
    case 'disputed':
      return { variant: 'destructive', label: 'Disputed' };
    case 'finalised':
      return { variant: 'success', label: 'Finalised' };
  }
};
